let defaultTimeout = 2000;
if (process.env.REST_TIMEOUT) {
    const parsedTimeout = parseInt(process.env.REST_TIMEOUT);
    if (!isNaN(parsedTimeout)) {
        defaultTimeout = parsedTimeout;
    }
}

/**
 * Make a http call to a Zoopla defined API (optionally passing a secret value)
 *
 * @param uri
 * @param body
 * @param okStatusCodes
 * @returns
 */
export async function zooplaPublicAPIFetch<T>(
    uri: { url: URL; secret?: string },
    body?: string,
    okStatusCodes: number[] = [],
    timeoutDuration: number = defaultTimeout
): Promise<T> {
    return fetch(
        uri.url,
        body,
        okStatusCodes,
        {
            'Content-Type': 'application/json',
            ...(uri.secret && { 'x-zoopla-secret-header': uri.secret })
        },
        timeoutDuration
    );
}

/**
 * Make a generic POST/GET HTTP request
 * @param url
 * @param body
 * @param okStatusCodes status codes that lead to a json response, otherwise error
 * @param headers
 * @param timeoutDuration
 * @returns
 */
export async function fetch<T>(
    url: URL,
    body?: string,
    okStatusCodes: number[] = [],
    headers?: Record<string, string>,
    timeoutDuration: number = defaultTimeout
): Promise<T> {
    try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), timeoutDuration);

        const init: RequestInit = {
            signal: controller.signal,
            headers: headers
        };

        if (body) {
            init.method = 'POST';
            init.body = body;
        }

        // Had to use toString here because msw doesn't intercept URL...
        const resp = await globalThis.fetch(url.toString(), init);

        clearTimeout(timeout);

        if (okStatusCodes.length > 0 && okStatusCodes.includes(resp?.status)) {
            return resp.json();
        }

        if (resp?.status >= 400) {
            const err = new Error(
                `${resp?.status} - ${resp.statusText}: ${url.toString()}`
            );
            err.name = 'non_ok_code';
            throw err;
        }

        return resp.json();
    } catch (e) {
        if (!(e instanceof Error)) {
            throw new Error('unknown');
        }

        if (e.name === 'AbortError') {
            throw new Error(`timeout: ${url.toString()}`);
        }

        if (e.name === 'non_ok_code') {
            throw e;
        }

        throw new Error(`${url.toString()}: ${e.message}`);
    }
}
